import React from 'react';

import LanguageConverterV2 from '@careerstart/wae-common/src/main/helperFunction/LanguageConverterV2';
import { Box, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { DashboardLoading, NoDataState } from '../../../components/LoadingStates';
import ScoreChart from '../../../components/ScoreChart';
import useFairReliabilityScore from '../../../utils/useFairReliabilityScore';
import Agencies from '../Agencies/Agencies';
import DashboardOnboarding from '../DashboardOnboarding';
import { DashboardWidgets } from '../shared';

import useAdminGlobalDataFetching from './hooks/useAdminGlobalDataFetching';

/**
 * GlobalDashboard component for admin users
 * - Displays sitewide data across all agencies
 * - Shows agencies list
 * - Only accessible to admins at /dashboard
 */
const GlobalDashboard = () => {
  const theme = useTheme();
  const { isWidgetDataLoading, widgetData } = useAdminGlobalDataFetching();
  const { pickReliability } = useFairReliabilityScore();
  const { workers, revenue, futurePlacementsData } = widgetData || {};

  // Admin revenue labels
  const revenueLabels = {
    title: LanguageConverterV2('dashboard.widget.admin.revenue.title'),
    totalRevenue: LanguageConverterV2('dashboard.widget.admin.revenue.total'),
    scheduled: LanguageConverterV2('dashboard.widget.admin.revenue.booked'),
  };

  if (isWidgetDataLoading) {
    return <DashboardLoading />;
  }

  if (!widgetData) {
    return <NoDataState />;
  }

  return (
    <Box
      padding={theme.components.dashboard.layout.padding}
      display="flex"
      flexDirection="column"
      gap={theme.components.dashboard.layout.sectionGap}
    >
      <Stack>
        <Stack direction="row" justifyContent="space-between" flexWrap="wrap" alignItems="center">
          <Typography sx={theme.components.dashboard.typography.header}>
            {LanguageConverterV2('dashboard.admin.title')}
          </Typography>
          {workers && (
            <ScoreChart
              label={LanguageConverterV2('dashboard.widget.reliability.title')}
              score={pickReliability(
                workers.averageRawReliability,
                workers.averageReliabilityScore
              )}
              description={LanguageConverterV2('dashboard.widget.reliability.description')}
            />
          )}
        </Stack>
        <Stack direction="row">
          <DashboardOnboarding />
        </Stack>
      </Stack>

      {/* Admin Dashboard Widgets */}
      <DashboardWidgets
        isLoading={isWidgetDataLoading}
        workers={workers}
        revenue={revenue}
        revenueLabels={revenueLabels}
        futurePlacementsData={futurePlacementsData}
      />

      {/* Agencies List */}
      <Agencies />
    </Box>
  );
};

export default GlobalDashboard;
